import { Accordion } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';

import { TradePlanDetailType } from '@/cip/_openapi/portfolio-optimizer';
import { booleanCol, dateTimeCol, moneyCol, numberCol, textCol } from '@/cip/widgets/Table/columnHelpers/colDef';

import { DetailsBlock } from './DetailsBlock';
import { GlobalBetaSellTradePlanDetails, GlobalBetaTradableLots, TradeDetailRecord } from './tradeDetailsModel';

export const GlobalBetaSellDetails = ({
    tradePlanDetails,
}: {
    tradePlanDetails: Partial<Record<TradePlanDetailType, TradeDetailRecord>>;
}) => {
    const tradableLots = tradePlanDetails[TradePlanDetailType.TRADABLE_LOTS]?.detailJson;
    const details = tradePlanDetails[TradePlanDetailType.DETAILS]?.detailJson;

    return (
        <Accordion allowMultiple>
            {tradableLots && (
                <DetailsBlock
                    data={tradableLots as GlobalBetaTradableLots[]}
                    columns={tradableLotsColumns}
                    columnSettingsKey="globalBetaSellDetailsTradableLots"
                    title="Tradable Lots"
                    initialSort={[
                        { id: 'lossRatio', desc: false },
                        { id: 'lastToSell', desc: false },
                    ]}
                />
            )}
            {details && (
                <DetailsBlock
                    data={details as GlobalBetaSellTradePlanDetails[]}
                    columns={detailsColumns}
                    columnSettingsKey="globalBetaSellDetails"
                    title="Details"
                    initialSort={{ id: 'sId', desc: false }}
                />
            )}
        </Accordion>
    );
};

const tradableLotsColumnHelper = createColumnHelper<GlobalBetaTradableLots>();
const tradableLotsColumns = [
    tradableLotsColumnHelper.accessor('allocationType', textCol({ id: 'allocationType' })),
    tradableLotsColumnHelper.accessor('sellLot', textCol({ id: 'sellLot' })),
    tradableLotsColumnHelper.accessor('lotQuantity', numberCol({ id: 'lotQuantity' })),
    tradableLotsColumnHelper.accessor('amountToSell', moneyCol({ id: 'amountToSell' })),
    tradableLotsColumnHelper.accessor('tradeQuantity', numberCol({ id: 'tradeQuantity' })),
    tradableLotsColumnHelper.accessor('price', moneyCol({ id: 'price' })),
    tradableLotsColumnHelper.accessor('postSellCumSum', moneyCol({ id: 'postSellCumSum' })),
    tradableLotsColumnHelper.accessor('sumPreSell', moneyCol({ id: 'sumPreSell' })),
    tradableLotsColumnHelper.accessor('gapToFill', moneyCol({ id: 'gapToFill' })),
    tradableLotsColumnHelper.accessor('tradeAmount', moneyCol({ id: 'tradeAmount' })),
    tradableLotsColumnHelper.accessor('existingPositionMarketValue', moneyCol({ id: 'existingPositionMarketValue' })),
    tradableLotsColumnHelper.accessor('quantity', numberCol({ id: 'quantity' })),
    tradableLotsColumnHelper.accessor('lastToSell', booleanCol({ id: 'lastToSell' })),
    tradableLotsColumnHelper.accessor('lossRatio', numberCol({ id: 'lossRatio' })),
    tradableLotsColumnHelper.accessor('sId', textCol({ id: 'sId' })),
];

const detailsColumnHelper = createColumnHelper<GlobalBetaSellTradePlanDetails>();
const detailsColumns = [
    detailsColumnHelper.accessor('allocationType', textCol({ id: 'allocationType' })),
    detailsColumnHelper.accessor('lotQuantity', numberCol({ id: 'lotQuantity' })),
    detailsColumnHelper.accessor('tradable', booleanCol({ id: 'tradable' })),
    detailsColumnHelper.accessor('notTradableReason', textCol({ id: 'notTradableReason' })),
    detailsColumnHelper.accessor('tradeAmount', numberCol({ id: 'tradeAmount' })),
    detailsColumnHelper.accessor('price', moneyCol({ id: 'price' })),
    detailsColumnHelper.accessor('tradeQuantity', numberCol({ id: 'tradeQuantity' })),
    detailsColumnHelper.accessor('clientDoNotSell', booleanCol({ id: 'clientDoNotSell' })),
    detailsColumnHelper.accessor('washSaleLastToSell', booleanCol({ id: 'washSaleLastToSell' })),
    detailsColumnHelper.accessor('hasLoss', booleanCol({ id: 'hasLoss' })),
    detailsColumnHelper.accessor('lastToSell', booleanCol({ id: 'lastToSell' })),
    detailsColumnHelper.accessor('minSellSize', moneyCol({ id: 'minSellSize' })),
    detailsColumnHelper.accessor('isAboveMinSellSize', booleanCol({ id: 'isAboveMinSellSize' })),
    detailsColumnHelper.accessor('targetExposure', moneyCol({ id: 'targetExposure' })),
    detailsColumnHelper.accessor(`minSellConstraint`, moneyCol({ id: 'minSellConstraint' })),
    detailsColumnHelper.accessor(`maxBuyConstraint`, moneyCol({ id: 'maxBuyConstraint' })),
    detailsColumnHelper.accessor('existingPositionMarketValue', moneyCol({ id: 'existingPositionMarketValue' })),
    detailsColumnHelper.accessor('costBasis', moneyCol({ id: 'costBasis' })),
    detailsColumnHelper.accessor('lossRatio', numberCol({ id: 'lossRatio' })),
    detailsColumnHelper.accessor('minSellAmount', moneyCol({ id: 'minSellAmount' })),
    detailsColumnHelper.accessor('minSellMultiplier', numberCol({ id: 'minSellMultiplier' })),
    detailsColumnHelper.accessor('openLotId', textCol({ id: 'openLotId' })),
    detailsColumnHelper.accessor('sId', textCol({ id: 'sId' })),
    detailsColumnHelper.accessor('bidPrice', moneyCol({ id: 'bidPrice' })),
    detailsColumnHelper.accessor('quotePrice', moneyCol({ id: 'quotePrice' })),
    detailsColumnHelper.accessor('priceSource', textCol({ id: 'priceSource' })),
    detailsColumnHelper.accessor('isPriceMissing', booleanCol({ id: 'isPriceMissing' })),
    detailsColumnHelper.accessor('priceTimestamp', dateTimeCol({ id: 'priceTimestamp' })),
];
